import { FC, ReactNode, useState } from 'react';
import { Student } from '../../../models/student.model';
import { useGet } from '../../../hooks/useApi';
import { Modal, Skeleton } from 'antd';
import { Table } from 'react-bootstrap';

import './EnrollmentsDiv.css';
import moment from 'moment';
import { Camp } from '../../../models/camp.model';
import { StudentProgram } from '../../../models/student-program.model';
import { StudentSchedule } from '../../../models/student-schedule.model';
import { NSDCamp } from '../../../models/nsd-camp.model';

interface EnrollmentsDivProps {
  student: Partial<Student> & { id: number };
}

type Response = {
  campEnrollments: Camp[];
  preschoolEnrollments: StudentProgram[];
  newPreschoolEnrollments: StudentSchedule[];
  galaxyEnrollments: StudentSchedule[];
  upcomingScheduleChanges: StudentProgram[];
  summerEnrollments: StudentSchedule[];
  elopEnrollments: StudentSchedule[];
  novaEnrollments: StudentSchedule[];
  NSDEnrollments: NSDCamp[];
};

const EnrollmentsDiv: FC<EnrollmentsDivProps> = ({ student }) => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalUrl, setEditModalUrl] = useState<string | undefined>();

  const enrollments = useGet<Response>(
    `/v1/auth/dashboard/student-enrollments/student/${student.id}`
  );

  const siteEmail =
    enrollments.data?.galaxyEnrollments[0]?.Schedule?.ProgramListing?.Location
      ?.School?.siteEmail || [];

  const starSite =
    enrollments.data?.galaxyEnrollments[0]?.Schedule?.ProgramListing?.Location
      ?.School?.shortName || [];

  const openModal = (
    e: React.MouseEvent,
    url = 'https://helpdesk.starsacramento.org/help/3712866477'
    // url = `mailto:${siteEmail}?subject=STAR Galaxy Schedule Change Request&body=Hello ${starSite} Staff,%0D%0A%0D%0AI would like to request a STAR Galaxy schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`
  ) => {
    e.preventDefault();
    setEditModalUrl(url);
    setEditModalVisible(true);
  };

  const getScheduleString = (object: any) => {
    const scheduleArray = [];

    if (object.monday || object.m) {
      scheduleArray.push('M');
    }

    if (object.tuesday || object.t) {
      scheduleArray.push('T');
    }
    if (object.wednesday || object.w) {
      scheduleArray.push('W');
    }
    if (object.thursday || object.th) {
      scheduleArray.push('Th');
    }
    if (object.friday || object.f) {
      scheduleArray.push('F');
    }

    return scheduleArray.join(', ');
  };

  const getNSDScheduleString = (days: { date: string | Date }[]): string => {
    const weekdayAbbreviations = ['M', 'T', 'W', 'Th', 'F'];
    const scheduleSet = new Set<string>();

    for (const object of days) {
      const weekday = moment(object.date).weekday();
      if (weekday >= 1 && weekday <= 5) {
        scheduleSet.add(weekdayAbbreviations[weekday - 1]);
      }
    }

    return Array.from(scheduleSet).join(', ');
  };

  const noEnrollments = (data: Response) => {
    for (const index in data) {
      // @ts-ignore
      if (data[index].length > 0) {
        return false;
      }
    }

    return true;
  };

  const ScheduleDiv: FC<{
    title: string;
    url?: string;
    children: ReactNode;
  }> = ({ title, url, children }) => {
    return (
      <div className={'enrollments-div mt-3'}>
        <div className={'pb-2'}>
          <table className={'w-100'}>
            <tr>
              <td style={{ verticalAlign: 'middle' }}>
                <h5 className={'p-0 m-0'}>{title}</h5>
              </td>
              <td className={'text-right'} style={{ verticalAlign: 'middle' }}>
                <small>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    className={'btn btn-sm btn-outline-primary'}
                    style={{ fontSize: '11px' }}
                    href="#"
                    onClick={(e) => openModal(e, url)}
                  >
                    Request Schedule Change
                  </a>
                </small>
              </td>
            </tr>
          </table>
        </div>

        {children}
      </div>
    );
  };

  const ScheduleDefaultLayout = (
    title: string,
    array: StudentSchedule[],
    url?: string
  ) => {
    if (!array.length) {
      return null;
    }

    return (
      <ScheduleDiv title={title} url={url}>
        <Table className={'text-center'} responsive>
          <thead>
            <tr>
              <th>Program</th>
              <th>School</th>
              <th>Title</th>
              <th style={{ minWidth: '120px' }}>Days</th>
            </tr>
          </thead>
          <tbody>
            {array.map((studentProgram) => (
              <tr key={studentProgram.Schedule?.ProgramListing?.id}>
                <td style={{ fontWeight: 700 }}>
                  {studentProgram.Schedule?.ProgramListing?.group}
                </td>
                <td>{studentProgram.Schedule?.ProgramListing?.subGroup}</td>
                <td>{studentProgram.Schedule?.name}</td>
                <td>{getScheduleString(studentProgram)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const ScheduleSummerLayout = (
    title: string,
    array: StudentSchedule[],
    url?: string
  ) => {
    if (!array.length) {
      return null;
    }

    return (
      <ScheduleDiv title={title} url={url}>
        <Table className={'text-center'} responsive>
          <thead>
            <tr>
              <th>Location</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Title</th>
              <th style={{ minWidth: '120px' }}>Days</th>
            </tr>
          </thead>
          <tbody>
            {array.map((studentProgram) => (
              <tr key={studentProgram.Schedule?.ProgramListing?.id}>
                <td style={{ fontWeight: 700 }}>
                  {studentProgram.Schedule?.ProgramListing?.generalTime}
                </td>
                <td>
                  {moment(
                    studentProgram.Schedule?.ProgramListing?.startDate
                  ).format('l')}
                </td>
                <td>
                  {moment(
                    studentProgram.Schedule?.ProgramListing?.endDate
                  ).format('l')}
                </td>
                <td>
                  {studentProgram.Schedule?.name ||
                    studentProgram.Schedule?.ProgramListing?.title}
                </td>
                <td>{getScheduleString(studentProgram)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const ScheduleNovaLayout = (
    title: string,
    array: StudentSchedule[],
    url?: string
  ) => {
    if (!array.length) {
      return null;
    }

    return (
      <ScheduleDiv title={title} url={url}>
        <Table className={'text-center'} responsive>
          <thead>
            <tr>
              <th>Location</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Title</th>
              <th style={{ minWidth: '120px' }}>Days</th>
            </tr>
          </thead>
          <tbody>
            {array.map((studentProgram) => (
              <tr key={studentProgram.Schedule?.ProgramListing?.id}>
                <td style={{ fontWeight: 700 }}>
                  {studentProgram.Schedule?.ProgramListing?.subGroup}
                </td>
                <td>
                  {moment(
                    studentProgram.Schedule?.ProgramListing?.startDate
                  ).format('l')}
                </td>
                <td>
                  {moment(
                    studentProgram.Schedule?.ProgramListing?.endDate
                  ).format('l')}
                </td>
                <td>
                  {studentProgram.Schedule?.name ||
                    studentProgram.Schedule?.ProgramListing?.title}
                </td>
                <td>{getScheduleString(studentProgram)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const ScheduleGalaxy = () =>
    ScheduleDefaultLayout(
      'STAR Galaxy',
      enrollments.data?.galaxyEnrollments || [],
      `mailto:${siteEmail}?subject=STAR Galaxy Schedule Change Request&body=Hello ${starSite} Staff,%0D%0A%0D%0AI would like to request a STAR Galaxy schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`
    );

  const ScheduleElop = () =>
    ScheduleDefaultLayout(
      'STAR ELO-P',
      enrollments.data?.elopEnrollments || []
    );

  const ScheduleNova = () =>
    ScheduleNovaLayout(
      'STAR Nova',
      enrollments.data?.novaEnrollments || [],
      `mailto:nova@starsacramento.org?subject=STAR Nova Schedule Change Request&body=Hello STAR Nova Staff,%0D%0A%0D%0AI would like to request a STAR Nova schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`
    );

  const ScheduleSummer = () =>
    ScheduleSummerLayout(
      'STAR Summer Camp',
      enrollments.data?.summerEnrollments || [],
      `mailto:techsupport@starsacramento.org?subject=STAR Summer Camp Schedule Change Request&body=Hello STAR Staff,%0D%0A%0D%0AI would like to request a STAR Summer Camp schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`
    );

  // Custom Layouts

  const ScheduleUpcomingGalaxy = () => {
    const upcomingScheduleChanges =
      enrollments.data?.upcomingScheduleChanges || [];

    if (!upcomingScheduleChanges.length) {
      return null;
    }

    return (
      <ScheduleDiv title={'Upcoming STAR Galaxy Schedule Changes'}>
        <Table responsive>
          <thead>
            <tr>
              <th>Schedule</th>
              <th style={{ minWidth: '120px' }}>Days</th>
              <th>Change</th>
              <th>Effective Date</th>
            </tr>
          </thead>
          <tbody>
            {upcomingScheduleChanges.map((studentProgram) => (
              <tr key={studentProgram.Program?.id}>
                <td style={{ fontWeight: 700 }}>
                  {`${studentProgram.Program?.title} @ ${studentProgram.Program?.Location?.School?.name}`}
                </td>
                <td>{getScheduleString(studentProgram)}</td>
                <td>{studentProgram.scheduleStart ? 'starting' : 'ending'}</td>
                <td>
                  {studentProgram.scheduleStart
                    ? moment(studentProgram.scheduleStart).format('MM/DD/YYYY')
                    : moment(studentProgram.disenrolled).format('MM/DD/YYYY')}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const ScheduleCamp = () => {
    const campEnrollments = enrollments.data?.campEnrollments || [];

    if (!campEnrollments.length) {
      return null;
    }

    return (
      <ScheduleDiv title={'STAR Non-School Day Camps'}>
        <Table responsive>
          <thead>
            <tr>
              <th>Camp</th>
              <th>Camp Location</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {campEnrollments.map((studentProgram) => (
              <tr key={`campEnrollments` + studentProgram.id}>
                <td style={{ fontWeight: 700 }}>{studentProgram.name}</td>
                <td>studentProgram.HostSchool.name</td>
                <td>
                  {studentProgram.CampDays?.length
                    ? moment(studentProgram.CampDays[0].date).format(
                        'MM/DD/YYYY'
                      )
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const SchedulePreschool = () => {
    const preschoolEnrollments = enrollments.data?.preschoolEnrollments || [];
    const newpreschoolEnrollments =
      enrollments.data?.newPreschoolEnrollments || [];

    if (!preschoolEnrollments.length && !newpreschoolEnrollments.length) {
      return null;
    }

    return (
      <ScheduleDiv title={'STAR Preschool'}>
        <Table>
          <thead>
            <tr>
              <th>Schedule</th>
            </tr>
          </thead>
          <tbody>
            {preschoolEnrollments.map((studentProgram) => (
              <tr key={studentProgram.Program?.id}>
                <td style={{ fontWeight: 700 }}>
                  {`${studentProgram.Program?.title} @ ${studentProgram.Program?.Location?.School?.name}`}
                </td>
              </tr>
            ))}
            {newpreschoolEnrollments.map((studentProgram) => (
              <tr key={studentProgram.Schedule?.ProgramListing?.id}>
                <td style={{ fontWeight: 700 }}>
                  {`${studentProgram.Schedule?.ProgramListing?.title} @ ${studentProgram.Schedule?.ProgramListing?.Location?.School?.name}`}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  const ScheduleNSD = () => {
    const NSDEnrollments = enrollments.data?.NSDEnrollments || [];

    if (!NSDEnrollments.length) {
      return null;
    }

    return (
      <ScheduleDiv
        title={'NSD Camps'}
        url={`mailto:${siteEmail}?subject=Non-School Day Schedule Change Request&body=Hello ${starSite} Staff,%0D%0A%0D%0AI would like to request a Non-School Day schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`}
      >
        <Table className={'text-center'} responsive>
          <thead>
            <tr>
              <th>Title</th>
              <th>Located At</th>
              <th style={{ minWidth: '120px' }}>Days</th>
            </tr>
          </thead>
          <tbody>
            {NSDEnrollments.map((studentProgram: any) => (
              <tr key={studentProgram.Schedule?.ProgramListing?.id}>
                <td>{studentProgram?.name}</td>
                <td>{studentProgram.School?.name}</td>
                <td>{getNSDScheduleString(studentProgram?.NSDCampDays)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScheduleDiv>
    );
  };

  if (enrollments.isLoading) {
    return <Skeleton active />;
  }

  if (enrollments.data && noEnrollments(enrollments.data)) {
    return (
      <div className="grid-x grid-margin-x grid-margin-y">
        <div className="cell large-auto">
          <div className="table-scroll pt-2">
            <h5 className={'m-0 p-0'}>Student Has No Current Enrollments</h5>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScheduleGalaxy />
      <ScheduleUpcomingGalaxy />
      <ScheduleElop />
      <ScheduleCamp />
      <ScheduleNova />
      <SchedulePreschool />
      <ScheduleSummer />
      <ScheduleNSD />

      <Modal
        title={'Schedule Change Request'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        {editModalUrl ==
        `mailto:${siteEmail}?subject=STAR Galaxy Schedule Change Request&body=Hello ${starSite} Staff,%0D%0A%0D%0AI would like to request a STAR Galaxy schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]` ? (
          <>
            <p className={'p-0 mb-2'}>
              To change this STAR Galaxy schedule, you will need to make a
              request through email directly to your STAR Site Director.
            </p>
            <p className={'p-0 mb-2'}>
              Be sure to include as much detail as possible about the schedule
              change you would like to make.
            </p>
            <p>
              If you encounter any issues using the link provided via the
              button, you can email{' '}
              <span className="text-success font-semibold">{siteEmail}</span> as
              an alternative.
            </p>
          </>
        ) : editModalUrl ==
          `mailto:${siteEmail}?subject=Non-School Day Schedule Change Request&body=Hello ${starSite} Staff,%0D%0A%0D%0AI would like to request a Non-School Day schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]` ? (
          <>
            <p className={'p-0 mb-2'}>
              To change this Non-School Day Camp schedule, you will need to make
              a request through email directly to your STAR Site Director.
            </p>
            <p className={'p-0 mb-2'}>
              Be sure to include as much detail as possible about the schedule
              change you would like to make.
            </p>
            <p>
              If you encounter any issues using the link provided via the
              button, you can email{' '}
              <span className="text-success font-semibold">{siteEmail}</span> as
              an alternative.
            </p>
          </>
        ) : editModalUrl ==
          `mailto:nova@starsacramento.org?subject=STAR Nova Schedule Change Request&body=Hello STAR Nova Staff,%0D%0A%0D%0AI would like to request a STAR Nova schedule change for my student, ${student.firstName} ${student.lastName}.%0D%0A%0D%0AHere are the details of the change I would like to make:%0D%0A%0D%0A[Please provide details about the schedule change you would like to make.]%0D%0A%0D%0AThank you for your assistance.%0D%0A%0D%0ABest regards,%0D%0A[Your Name]` ? (
          <>
            <p className="p-0 mb-2">
              To make changes for this Nova class, you will need to make a
              request through email directly to the STAR Nova department.
            </p>
            <p className={'p-0 mb-2'}>
              Be sure to include as much detail as possible about the change you
              would like to make.
            </p>
            <p>
              If you encounter any issues using the link provided via the
              button, you can email{' '}
              <span className="text-success font-semibold">
                nova@starsacramento.org
              </span>{' '}
              as an alternative.
            </p>
          </>
        ) : (
          <>
            <p className="p-0 mb-2">
              To change this schedule, you will need to make a request with STAR
              Tech Support through the Tech Support contact form.
            </p>
            <p>
              Be sure to include the week of the camp or the dates of the days
              that you would to add or cancel.
            </p>
          </>
        )}

        <hr className={'p-0 mt-3 mb-4'} />

        <a
          className="btn btn-primary w-100"
          href={editModalUrl}
          target={'_blank'}
          rel="noreferrer"
        >
          Submit Request
        </a>
      </Modal>
    </>
  );
};

export default EnrollmentsDiv;
